import { Divider } from "antd";

import { BTText } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { CurrencyDisplay } from "commonComponents/financial/CurrencyDisplay/CurrencyDisplay";
import { PercentageDisplay } from "commonComponents/financial/PercentageDisplay/PercentageDisplay";

import "./PercentInvoicedBreakdown.less";

export interface IPercentInvoicedBreakdown {
    invoicedAmount: number;
    afterRetainagePercent: number;
    retainageHeld: number;
    retainageHeldPercent: number;
    workToComplete: number;
    workToCompletePercent: number;
    clientPrice: number;
    showPercentColors: boolean;
}

interface IPercentInvoicedBreakdownProps {
    breakdown: IPercentInvoicedBreakdown;
    "data-testid"?: string;
}
type RowType = "bold" | "indented" | "subtotal";

/** Mirrors the PercentageDisplayBar section colors rendered by PercentInvoicedCell. */
type RowColor = "blue" | "yellow" | "grey";

interface IBreakdownRowProps {
    rowType: RowType;
    title: string;
    percent: number;
    amount: number;
    testId: string;
    showPercentColors?: boolean;
    color?: RowColor;
}

function BreakdownRow({
    rowType,
    title,
    percent,
    amount,
    testId,
    showPercentColors,
    color,
}: IBreakdownRowProps) {
    const isBold = rowType === "bold";
    const isIndented = rowType === "indented";
    const rowClass = cn("bds-gap-4 bds-justify-between bds-w-full", { indentedRow: isIndented });
    const titleClass = cn({ indentedTitle: isIndented, boldTitle: isBold });
    const valueClass = cn({ boldTitle: isBold });

    const titleContent =
        showPercentColors && color ? (
            <div className="flex flex-row gap-4 items-center">
                <span className={cn("colorChip", color)} data-testid={`${testId}-colorChip`} />
                {title}
            </div>
        ) : (
            title
        );

    return (
        <div className={cn("flex flex-row", rowClass)} data-testid={`${testId}-row`}>
            <BTText
                testid={`${testId}-title`}
                size="normal-sm"
                className={titleClass}
                text={titleContent}
            />
            <div className="flex flex-row gap-4">
                <BTText
                    testid={`${testId}-percent`}
                    size="normal-sm"
                    className={valueClass}
                    text={
                        <PercentageDisplay value={percent} data-testid={`${testId}-percentValue`} />
                    }
                />
                <BTText
                    testid={`${testId}-amount`}
                    size="normal-sm"
                    className={valueClass}
                    text={<CurrencyDisplay value={amount} />}
                />
            </div>
        </div>
    );
}

export function PercentInvoicedBreakdown({
    breakdown,
    "data-testid": dataTestId = "percentInvoicedBreakdown",
}: IPercentInvoicedBreakdownProps) {
    const remainingAmount = breakdown.retainageHeld + breakdown.workToComplete;
    const remainingPercent = breakdown.retainageHeldPercent + breakdown.workToCompletePercent;

    return (
        <div className="flex flex-col percentInvoicedBreakdown" data-testid={dataTestId}>
            <BreakdownRow
                rowType="bold"
                title="Invoiced"
                percent={breakdown.afterRetainagePercent}
                amount={breakdown.invoicedAmount}
                testId={`${dataTestId}-invoiced`}
                showPercentColors={breakdown.showPercentColors}
                color="blue"
            />

            <Divider className="tooltipDivider" />

            <BTText
                size="normal-sm"
                text="Remaining to invoice"
                testid={`${dataTestId}-remainingLabel`}
            />
            <BreakdownRow
                rowType="indented"
                title="Retainage held"
                percent={breakdown.retainageHeldPercent}
                amount={breakdown.retainageHeld}
                testId={`${dataTestId}-retainageHeld`}
                showPercentColors={breakdown.showPercentColors}
                color="yellow"
            />
            <BreakdownRow
                rowType="indented"
                title="Work to be completed"
                percent={breakdown.workToCompletePercent}
                amount={breakdown.workToComplete}
                testId={`${dataTestId}-workToComplete`}
                showPercentColors={breakdown.showPercentColors}
                color="grey"
            />

            <Divider className="tooltipDivider tooltipDividerLight" />

            <BreakdownRow
                rowType="subtotal"
                title=""
                percent={remainingPercent}
                amount={remainingAmount}
                testId={`${dataTestId}-remainingSubtotal`}
            />

            <Divider className="tooltipDivider" />

            <BreakdownRow
                rowType="bold"
                title="Client price"
                percent={100}
                amount={breakdown.clientPrice}
                testId={`${dataTestId}-clientPrice`}
            />
        </div>
    );
}
